"use client";

import {useContext, useEffect, useState} from "react";
import {ApprovalLookupBudgetContext} from "./ApprovalLookupBudget";

type LookupEntry<T> = {promise: Promise<T>; readers: number};

function releaseLookupEntry<T>(entries: Map<string, LookupEntry<T>>, key: string, entry: LookupEntry<T>) {
   entry.readers--;
   // Preserve Strict Mode's immediate remount, without keeping stale data after navigation.
   queueMicrotask(() => {
      if (!entry.readers && entries.get(key) === entry) entries.delete(key);
   });
}

/** Share reference data only while reviews use it; keys include organization and API version. */
export function createSharedApprovalLookup<T>(fetcher: (key: string) => Promise<T>) {
   const entries = new Map<string, LookupEntry<T>>();
   return function useSharedApprovalLookup(key: string | null): T | undefined {
      const reserveLookup = useContext(ApprovalLookupBudgetContext);
      const [result, setResult] = useState<{key: string; value: T} | null>(null);
      useEffect(() => {
         if (!key) return;
         let active = true;
         let entry = entries.get(key);
         if (!entry) {
            if (reserveLookup && !reserveLookup()) return;
            entry = {promise: fetcher(key), readers: 0};
            entries.set(key, entry);
         }
         const current = entry;
         current.readers++;
         void current.promise.then(value => {
            if (active) setResult({key, value});
         }).catch(() => {
            if (active) setResult(null);
            // A later reader can retry a failed request.
            if (entries.get(key) === current) entries.delete(key);
         });
         return () => {
            active = false;
            releaseLookupEntry(entries, key, current);
         };
      }, [key, reserveLookup]);
      return key && result?.key === key ? result.value : undefined;
   };
}
